/**
 * Segmented control — pick exactly one option from a small set of peers.
 *
 * Extracted from the consolidated Performance surface's platform scope switch
 * (#5971) so the idiom has one home: muted track, active segment lifted with
 * background + shadow, optional leading icon per option (e.g. PlatformLogo).
 * Use this instead of ToggleSwitch when the choice is between peers rather
 * than an on/off state.
 */

import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

export interface SegmentedControlOption<T extends string> {
  value: T;
  label: string;
  icon?: ReactNode;
}

interface SegmentedControlProps<T extends string> {
  options: SegmentedControlOption<T>[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel: string;
  disabled?: boolean;
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  disabled = false,
}: SegmentedControlProps<T>) {
  return (
    <div
      className="inline-flex h-9 items-center rounded-lg bg-muted p-1 text-gray-700"
      role="tablist"
      aria-label={ariaLabel}
    >
      {options.map((option) => {
        const active = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={active}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            className={cn(
              'inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium transition-all',
              active ? 'bg-background text-foreground shadow' : 'text-gray-700 hover:text-gray-900',
              disabled && 'opacity-50 cursor-not-allowed',
            )}
          >
            {option.icon}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
